/* ============================================================================
   DEL TORO PRODUCT PAGE JS - OPTIMIZED & MODULAR
   Following KISS, YAGNI, and SOLID principles
   ============================================================================ */

(function() {
    'use strict';
    
    // ========================================================================
    // CONFIGURATION
    // ========================================================================
    const CONFIG = {
        AVAILABILITY_UPDATE_INTERVAL: 30 * 60 * 1000, // 30 minutes
        MOBILE_BREAKPOINT: 768, // iPad portrait width
        THROTTLE_DELAY: 16, // ~60fps refresh rate
        DEBOUNCE_DELAY: 100,
        SUBSCRIPTION_DISCOUNT: 0.10
    };
    
    // ========================================================================
    // UTILITIES
    // ========================================================================
    const utils = {
        debounce(func, wait) {
            let timeout;
            return function(...args) {
                clearTimeout(timeout);
                timeout = setTimeout(() => func.apply(this, args), wait);
            };
        },
        
        throttle(func, limit) {
            let inThrottle;
            return function(...args) {
                if (!inThrottle) {
                    func.apply(this, args);
                    inThrottle = true;
                    setTimeout(() => inThrottle = false, limit);
                }
            };
        },
        
        isMobile() {
            return window.innerWidth <= CONFIG.MOBILE_BREAKPOINT;
        },
        
        formatPrice(price) {
            return new Intl.NumberFormat('en-AU', {
                style: 'currency',
                currency: 'AUD'
            }).format(price);
        }
    };
    
    // ========================================================================
    // HERO SECTION
    // ========================================================================
    const heroSection = {
        init() {
            this.initScrollToProduct();
            this.initAvailability();
        },
        
        initScrollToProduct() {
            window.scrollToProduct = function() {
                const productSection = document.querySelector('.dt-product-section, #product, [data-section-type="product"]');
                if (productSection) {
                    productSection.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                } else {
                    window.scrollBy({
                        top: window.innerHeight,
                        behavior: 'smooth'
                    });
                }
            };
        },
        
        initAvailability() {
            const updateAvailability = () => {
                const availabilityEl = document.querySelector('.dt-limited-availability strong');
                if (!availabilityEl) return;
                
                // Simple countdown: 200 bags - (hour of day * 8)
                const hour = new Date().getHours();
                const remaining = Math.max(200 - (hour * 8), 15);
                
                availabilityEl.textContent = `${remaining} bags`;
            };
            
            updateAvailability();
            setInterval(updateAvailability, CONFIG.AVAILABILITY_UPDATE_INTERVAL);
        }
    };
    
    // ========================================================================
    // UNIVERSAL SLIDER - Single implementation for all sliders
    // ========================================================================
    const createSlider = (container, options = {}) => {
        if (!container) return;
        
        const {
            itemSelector = '.dt-badge-item',
            dotsContainer = null,
            mobileOnly = false
        } = options;
        
        // Find dots container
        const dotsElement = dotsContainer || container.closest('section')?.querySelector('.dt-slider-dots');
        const dots = dotsElement?.querySelectorAll('.dt-slider-dot') || [];
        
        if (!dots.length) return;
        
        // Handle mobile-only display
        const updateDisplay = () => {
            const isMobileView = utils.isMobile();
            
            if (mobileOnly) {
                // Show/hide dots based on screen size
                if (dotsElement) {
                    dotsElement.style.display = isMobileView ? 'flex' : 'none';
                }
                
                // Reset container transform on desktop
                if (!isMobileView) {
                    container.style.transform = '';
                    return false; // Don't initialize slider functionality
                }
            }
            
            return true; // Initialize slider functionality
        };
        
        // Initial display check
        if (!updateDisplay()) return;
        
        // Update active dot based on scroll position
        const updateActiveDot = () => {
            const scrollLeft = container.scrollLeft;
            const item = container.querySelector(itemSelector);
            if (!item) return;
            
            const itemWidth = item.offsetWidth + parseInt(window.getComputedStyle(item).marginRight || 16);
            const activeIndex = Math.round(scrollLeft / itemWidth);
            
            dots.forEach((dot, index) => {
                dot.classList.toggle('active', index === activeIndex);
            });
        };
        
        // Dot click handlers
        dots.forEach((dot, index) => {
            dot.addEventListener('click', () => {
                const items = container.querySelectorAll(itemSelector);
                const targetItem = items[index];
                if (targetItem) {
                    targetItem.scrollIntoView({
                        behavior: 'smooth',
                        inline: index === 0 ? 'start' : 'center',
                        block: 'nearest'
                    });
                }
            });
        });
        
        // Listen to scroll
        const handleScroll = utils.throttle(updateActiveDot, 50);
        container.addEventListener('scroll', handleScroll, { passive: true });
        
        // Initial update
        updateActiveDot();
        
        // Handle resize for mobile-only sliders
        if (mobileOnly) {
            const handleResize = utils.debounce(() => {
                updateDisplay();
            }, 250);
            window.addEventListener('resize', handleResize);
        }
    };
    
    // ========================================================================
    // SLIDER MANAGER - Initialize all sliders
    // ========================================================================
    const sliderManager = {
        init() {
            // Accreditations slider (mobile only)
            const accreditationsTrack = document.getElementById('accreditationsTrack');
            if (accreditationsTrack) {
                const dotsContainer = document.querySelector('.dt-accreditations-section .dt-slider-dots');
                createSlider(accreditationsTrack, {
                    itemSelector: '.dt-accreditation-card',
                    dotsContainer: dotsContainer,
                    mobileOnly: true
                });
            }
            
            // Main badges slider (mobile only)
            const badgesSlider = document.getElementById('badgesSlider');
            if (badgesSlider) {
                const dotsContainer = document.getElementById('sliderDots');
                createSlider(badgesSlider, {
                    itemSelector: '.dt-badge-item',
                    dotsContainer: dotsContainer,
                    mobileOnly: true
                });
            }
            
            // Certifications badges slider (mobile only)
            const certBadgesSlider = document.getElementById('certBadgesSlider');
            if (certBadgesSlider) {
                const dotsContainer = certBadgesSlider.closest('.dt-certifications-variant')?.querySelector('.dt-slider-dots');
                createSlider(certBadgesSlider, {
                    itemSelector: '.dt-badge-item',
                    dotsContainer: dotsContainer,
                    mobileOnly: true
                });
            }
        }
    };
    
    // ========================================================================
    // VIDEO AUTOPLAY
    // ========================================================================
    const videoManager = {
        init() {
            const video = document.querySelector('.dt-hero-video-element');
            if (!video) return;
            
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        video.play().catch(err => {
                            console.log('Video autoplay prevented:', err);
                        });
                    } else {
                        video.pause();
                    }
                });
            }, { 
                threshold: 0.25,
                rootMargin: '0px'
            });
            
            observer.observe(video);
        }
    };
    
    // ========================================================================
    // PRODUCT GALLERY - Handles image carousel
    // ========================================================================
    const ProductGallery = {
        currentSlide: 0,
        elements: {},
        
        init() {
            const section = document.querySelector('.dt-product-section');
            if (!section) return;
            
            this.elements = {
                thumbnails: section.querySelectorAll('.dt-thumbnail'),
                carouselItems: section.querySelectorAll('.dt-carousel-item'),
                indicators: section.querySelectorAll('.dt-indicator')
            };
            
            if (this.elements.thumbnails.length === 0) return;
            
            this.attachEventListeners();
        },
        
        attachEventListeners() {
            this.elements.thumbnails.forEach((thumb, index) => {
                thumb.addEventListener('click', () => {
                    this.showSlide(index);
                });
            });
            
            this.elements.indicators.forEach((indicator, index) => {
                indicator.addEventListener('click', () => {
                    this.showSlide(index);
                });
            });
        },
        
        showSlide(index) {
            this.currentSlide = index;
            
            // Update thumbnails
            this.elements.thumbnails.forEach((thumb, i) => {
                if (i === index) {
                    thumb.classList.add('active');
                } else {
                    thumb.classList.remove('active');
                }
            });
            
            // Update carousel items
            this.elements.carouselItems.forEach((item, i) => {
                if (i === index) {
                    item.classList.add('active');
                } else {
                    item.classList.remove('active');
                }
            });
            
            // Update indicators
            this.elements.indicators.forEach((indicator, i) => {
                if (i === index) {
                    indicator.classList.add('active');
                } else {
                    indicator.classList.remove('active');
                }
            });
        }
    };
    
    // ========================================================================
    // PRODUCT PRICING - Handles price calculations with variants
    // ========================================================================
    const ProductPricing = {
        elements: {},
        state: {
            basePrice: 20.00,
            isSubscription: false
        },
        
        init() {
            const section = document.querySelector('.dt-product-section');
            if (!section) return;
            
            this.elements = {
                mainPrice: section.querySelector('.dt-price'),
                onetimePrice: section.querySelector('[data-onetime-price]'),
                subscribePrice: section.querySelector('[data-subscribe-price]'),
                originalSubscribePrice: section.querySelector('[data-original-price]')
            };
            
            // Get initial price from the HTML data attribute
            if (this.elements.mainPrice) {
                const initialPrice = parseFloat(this.elements.mainPrice.dataset.price);
                if (!isNaN(initialPrice)) {
                    this.state.basePrice = initialPrice;
                }
            }
            
            this.update();
        },
        
        setPrice(price) {
            this.state.basePrice = parseFloat(price);
            this.update();
        },
        
        setSubscription(isSubscription) {
            this.state.isSubscription = isSubscription;
            this.update();
        },
        
        update() {
            try {
                const { basePrice, isSubscription } = this.state;
                const subscribePrice = basePrice * (1 - CONFIG.SUBSCRIPTION_DISCOUNT);
                const finalPrice = isSubscription ? subscribePrice : basePrice;
                
                // Update main price
                if (this.elements.mainPrice) {
                    this.elements.mainPrice.textContent = utils.formatPrice(finalPrice);
                }
                
                // Update one-time price
                if (this.elements.onetimePrice) {
                    this.elements.onetimePrice.textContent = utils.formatPrice(basePrice);
                    this.elements.onetimePrice.setAttribute('data-onetime-price', basePrice.toFixed(2));
                }
                
                // Update subscribe price
                if (this.elements.subscribePrice) {
                    this.elements.subscribePrice.textContent = utils.formatPrice(subscribePrice);
                    this.elements.subscribePrice.setAttribute('data-subscribe-price', subscribePrice.toFixed(2));
                }
                
                // Update original subscribe price (strikethrough)
                if (this.elements.originalSubscribePrice) {
                    this.elements.originalSubscribePrice.textContent = utils.formatPrice(basePrice);
                    this.elements.originalSubscribePrice.setAttribute('data-original-price', basePrice.toFixed(2));
                }
            } catch (error) {
                console.error('Error updating prices:', error);
            }
        }
    };
    
    // ========================================================================
    // PRODUCT OPTIONS - Handles variant selection and purchase type
    // ========================================================================
    const ProductOptions = {
        elements: {},
        
        init() {
            const section = document.querySelector('.dt-product-section');
            if (!section) return;
            
            this.elements = {
                weightCards: section.querySelectorAll('.dt-weight-card'),
                purchaseInputs: section.querySelectorAll('input[name="purchase_option"]'),
                variantIdInput: section.querySelector('#variant_id'),
                quantityInput: section.querySelector('.dt-quantity-input'),
                sellingPlanInput: section.querySelector('#selling_plan_input'),
                sellingPlanIdInput: section.querySelector('input[name="selling_plan_id"]')
            };
            
            if (this.elements.weightCards.length === 0) return;
            
            this.attachEventListeners();
            this.initFromURL();
        },
        
        initFromURL() {
            // Check if there's a variant ID in the URL
            const urlParams = new URLSearchParams(window.location.search);
            const variantId = urlParams.get('variant');
            
            if (variantId) {
                // Find the card with this variant ID
                const targetCard = Array.from(this.elements.weightCards).find(
                    card => card.dataset.variantId === variantId
                );
                
                if (targetCard) {
                    // Only select if it's not already active
                    if (!targetCard.classList.contains('active')) {
                        this.selectWeight(targetCard);
                    }
                }
            }
        },
        
        attachEventListeners() {
            // Weight/variant selection
            this.elements.weightCards.forEach(card => {
                card.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.selectWeight(card);
                });
            });
            
            // Purchase option (one-time vs subscription)
            this.elements.purchaseInputs.forEach(input => {
                input.addEventListener('change', () => {
                    this.selectPurchaseOption(input);
                });
            });
        },
        
        selectWeight(card) {
            // Update active state
            this.elements.weightCards.forEach(c => {
                c.classList.remove('active');
                c.setAttribute('aria-checked', 'false');
            });
            
            card.classList.add('active');
            card.setAttribute('aria-checked', 'true');
            
            // Get variant data
            const variantId = card.dataset.variantId;
            const price = parseFloat(card.dataset.price);
            
            // Update variant ID input
            if (this.elements.variantIdInput && variantId) {
                this.elements.variantIdInput.value = variantId;
            }
            
            // Update quantity input - always 1 since we're using real variants
            if (this.elements.quantityInput) {
                this.elements.quantityInput.value = 1;
            }
            
            // Update pricing
            if (ProductPricing && ProductPricing.setPrice) {
                ProductPricing.setPrice(price);
            }
        },
        
        selectPurchaseOption(input) {
            const isSubscription = input.value === 'subscription';
            
            // Update selling plan input
            if (this.elements.sellingPlanInput) {
                if (isSubscription && this.elements.sellingPlanIdInput) {
                    this.elements.sellingPlanInput.value = this.elements.sellingPlanIdInput.value;
                } else {
                    this.elements.sellingPlanInput.value = '';
                }
            }
            
            // Update pricing
            if (ProductPricing && ProductPricing.setSubscription) {
                ProductPricing.setSubscription(isSubscription);
            }
        }
    };
    
    // ========================================================================
    // PRODUCT FORM - Handles add to cart
    // ========================================================================
    const ProductForm = {
        elements: {},
        
        init() {
            const section = document.querySelector('.dt-product-section');
            if (!section) return;
            
            this.elements = {
                form: section.querySelector('.dt-product-form'),
                variantIdInput: section.querySelector('#variant_id'),
                quantityInput: section.querySelector('.dt-quantity-input'),
                sellingPlanInput: section.querySelector('#selling_plan_input'),
                addToCartBtn: section.querySelector('.dt-add-to-cart')
            };
            
            if (this.elements.form) {
                this.elements.form.addEventListener('submit', (e) => this.handleSubmit(e));
            }
        },
        
        async handleSubmit(e) {
            e.preventDefault();
            
            // Validate variant
            if (!this.elements.variantIdInput || !this.elements.variantIdInput.value) {
                alert('Please select a product variant');
                return;
            }
            
            // Update button state
            const btn = this.elements.addToCartBtn;
            const originalText = btn.innerHTML;
            btn.disabled = true;
            btn.innerHTML = '<span class="dt-spinner dt-animate-spin"></span>Processing...';
            
            try {
                // Prepare form data
                const formData = {
                    id: this.elements.variantIdInput.value,
                    quantity: this.elements.quantityInput ? this.elements.quantityInput.value : 1
                };
                
                // Add selling plan if subscription
                if (this.elements.sellingPlanInput && this.elements.sellingPlanInput.value) {
                    formData.selling_plan = this.elements.sellingPlanInput.value;
                }
                
                // Submit to cart
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: JSON.stringify(formData)
                });
                
                if (response.ok) {
                    btn.innerHTML = '<span class="dt-spinner dt-animate-spin"></span>Redirecting to checkout...';
                    window.location.href = '/checkout';
                } else {
                    throw new Error('Failed to add product to cart');
                }
            } catch (error) {
                console.error('Error adding to cart:', error);
                btn.disabled = false;
                btn.innerHTML = originalText;
                alert('There was an error adding the product to cart. Please try again.');
            }
        }
    };
    
    // ========================================================================
    // STICKY CTA BAR
    // ========================================================================
    const stickyCTA = {
        init() {
            const stickyBar = document.getElementById('dt-sticky-cta');
            const productSection = document.querySelector('.dt-product-section, #del-toro-product-hero');
            
            if (!stickyBar || !productSection) return;
            
            let isBarVisible = false;
            
            const checkScroll = () => {
                const productRect = productSection.getBoundingClientRect();
                
                if (productRect.bottom < -50 && !isBarVisible) {
                    stickyBar.classList.add('show');
                    isBarVisible = true;
                } else if (productRect.bottom >= -50 && isBarVisible) {
                    stickyBar.classList.remove('show');
                    isBarVisible = false;
                }
            };
            
            const throttledCheckScroll = utils.throttle(checkScroll, 100);
            window.addEventListener('scroll', throttledCheckScroll, { passive: true });
            checkScroll();
        }
    };
    
    // ========================================================================
    // REVIEWS SCROLL ANIMATION
    // ========================================================================
    const reviewsScroll = {
        init() {
            const grid = document.getElementById('reviews-scroll');
            if (!grid) return;
            
            const cards = grid.querySelectorAll('.dt-masonry-card');
            let touchPaused = false;
            
            // Pause on hover
            cards.forEach(card => {
                card.addEventListener('mouseenter', () => {
                    grid.style.animationPlayState = 'paused';
                });
                card.addEventListener('mouseleave', () => {
                    grid.style.animationPlayState = 'running';
                });
            });
            
            // Pause on touch
            grid.addEventListener('touchstart', () => {
                grid.style.animationPlayState = 'paused';
                touchPaused = true;
            });
            
            grid.addEventListener('touchend', () => {
                setTimeout(() => {
                    if (touchPaused) {
                        grid.style.animationPlayState = 'running';
                        touchPaused = false;
                    }
                }, 3000);
            });
        }
    };
    
    // ========================================================================
    // INITIALIZATION - Main entry point
    // ========================================================================
    document.addEventListener('DOMContentLoaded', function() {
        heroSection.init();
        sliderManager.init();
        videoManager.init();
        ProductGallery.init();
        ProductPricing.init();
        ProductOptions.init();
        ProductForm.init();
        stickyCTA.init();
        reviewsScroll.init();
    });
    
})();